import React, { useEffect, useRef } from "react";
import { Sparkles, X, ChevronDown } from "lucide-react";
import { AI_HELP_KB_MAX_LENGTH } from "../../utils/flowAiHelpUtils";

function useClickOutside(ref, onClose, isOpen) {
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [ref, onClose, isOpen]);
}

export default function FlowAiHelpMenu({
  open,
  onOpenChange,
  allowAiHelpDuringConversation,
  onAllowAiHelpChange,
  aiHelpKnowledgeBase,
  onKnowledgeBaseChange,
  error,
}) {
  const ref = useRef(null);
  useClickOutside(ref, () => onOpenChange(false), open);

  const kbLength = (aiHelpKnowledgeBase || "").length;
  const kbNearLimit = kbLength > AI_HELP_KB_MAX_LENGTH * 0.9;

  const handleKbChange = (value) => {
    if (value.length <= AI_HELP_KB_MAX_LENGTH) {
      onKnowledgeBaseChange(value);
    }
  };

  const toggleOpen = () => onOpenChange(!open);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggleOpen}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border transition-colors ${
          open || allowAiHelpDuringConversation
            ? "bg-indigo-50 border-indigo-300 text-indigo-700"
            : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-indigo-50 hover:border-indigo-200"
        }`}
        title="Flow AI help settings"
      >
        <Sparkles size={14} />
        <span className="font-bold text-xs">AI Help</span>
        {allowAiHelpDuringConversation && (
          <span className="text-[9px] font-bold bg-indigo-500 text-white px-1.5 py-0.5 rounded-full">
            ON
          </span>
        )}
        <ChevronDown
          size={12}
          className={`opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 w-[min(380px,calc(100vw-2rem))] bg-white border border-gray-200 rounded-xl shadow-xl z-[200] overflow-hidden"
          role="dialog"
          aria-label="Flow AI Help settings"
        >
          <div className="flex items-start justify-between gap-2 px-3.5 py-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 to-white">
            <div className="flex items-start gap-2 min-w-0">
              <Sparkles size={15} className="text-indigo-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-gray-900 leading-tight">Flow AI Help</h3>
                <p className="text-[10px] text-gray-500 mt-0.5 leading-snug">
                  Answers off-topic questions mid-flow, then resumes the same step.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 flex-shrink-0"
              title="Close"
            >
              <X size={14} />
            </button>
          </div>

          <div className="p-3 space-y-2.5 max-h-[min(65vh,480px)] overflow-y-auto">
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
              <span className="text-xs font-semibold text-gray-800 leading-snug">
                Allow AI help during conversation
              </span>
              <button
                type="button"
                onClick={() => onAllowAiHelpChange(!allowAiHelpDuringConversation)}
                className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${
                  allowAiHelpDuringConversation ? "bg-indigo-500" : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    allowAiHelpDuringConversation ? "left-[18px]" : "left-0.5"
                  }`}
                />
              </button>
            </div>

            {allowAiHelpDuringConversation && (
              <div>
                <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wide mb-1 block">
                  Knowledge base context
                </label>
                <textarea
                  value={aiHelpKnowledgeBase}
                  onChange={(e) => handleKbChange(e.target.value)}
                  placeholder="FAQ, return policy, pricing, support hours..."
                  rows={4}
                  className="w-full px-2.5 py-2 text-xs bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 placeholder:text-gray-400 resize-none"
                />
                <span
                  className={`text-[10px] font-semibold mt-1 block ${
                    kbNearLimit ? "text-amber-600" : "text-gray-400"
                  }`}
                >
                  {kbLength.toLocaleString()} / {AI_HELP_KB_MAX_LENGTH.toLocaleString()}
                </span>
              </div>
            )}

            {error && (
              <p className="text-[11px] text-red-600 font-medium px-0.5">{error}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
